/**
 * Typed wrappers around the resources / folders REST API.
 *
 * Endpoint reference (mounted at `/api/v1/admin/...` server-side; the
 * `apiGet` helper transparently prepends `/api`, so we pass `/v1/...`):
 *
 *   GET  /v1/admin/resources                 (list, paginated envelope)
 *   GET  /v1/admin/resources/:id             (detail with current_version)
 *   POST /v1/admin/resources                 (multipart static upload)
 *   POST /v1/admin/resources/dynamic         (JSON dynamic create)
 *   GET  /v1/admin/folders                   (flat list for pickers)
 *
 * Static upload is multipart/form-data and reports progress, so it uses
 * XHR directly (fetch has no upload-progress event in browsers); it does
 * NOT route through `apiPost` (which forces a JSON content-type).
 */

import type {
  AuditEventSummary,
  DynamicResourceCreate,
  NameI18n,
  ResourceListQuery,
  ResourceSummary,
  ResourceVersionSummary,
} from '@displaycms/contracts';
import type { ResourceKind, ResourceState } from '@displaycms/contracts';

import { apiGet, apiPatch, apiPost, ApiError } from '@/api/client';

// ---------------------------------------------------------------------------
// List endpoint
// ---------------------------------------------------------------------------

/**
 * Server's list envelope. Mirrors `ResourceListResult` in
 * `apps/server/src/resources/resources.service.ts`; not in
 * `@displaycms/contracts` (Phase 1 keeps response envelopes server-local).
 */
export interface ResourceListResponse {
  items: ResourceSummary[];
  total: number;
  page: number;
  per_page: number;
}

/** Detail response: a `ResourceSummary` plus its current version (if any). */
export interface ResourceDetail extends ResourceSummary {
  current_version: ResourceVersionSummary | null;
}

function buildListQuery(query: Partial<ResourceListQuery>): string {
  const params = new URLSearchParams();
  if (query.folder) params.set('folder', query.folder);
  if (query.kind) params.set('kind', query.kind);
  if (query.state) params.set('state', query.state);
  if (query.search) params.set('search', query.search);
  if (query.page !== undefined) params.set('page', String(query.page));
  if (query.per_page !== undefined) params.set('per_page', String(query.per_page));
  const s = params.toString();
  return s.length > 0 ? `?${s}` : '';
}

export function listResources(
  query: Partial<ResourceListQuery> = {},
  options?: { signal?: AbortSignal },
): Promise<ResourceListResponse> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  return apiGet<ResourceListResponse>(
    `/v1/admin/resources${buildListQuery(query)}`,
    opts,
  );
}

export function getResource(
  id: string,
  options?: { signal?: AbortSignal },
): Promise<ResourceDetail> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  return apiGet<ResourceDetail>(`/v1/admin/resources/${id}`, opts);
}

// ---------------------------------------------------------------------------
// Folder picker
// ---------------------------------------------------------------------------

export interface FolderSummary {
  id: string;
  tenant_id: string;
  parent_id: string | null;
  name_i18n: NameI18n;
  created_at: string;
}

export interface FolderListResponse {
  items: FolderSummary[];
}

export function listFolders(
  options?: { signal?: AbortSignal },
): Promise<FolderListResponse> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  return apiGet<FolderListResponse>('/v1/admin/folders', opts);
}

// ---------------------------------------------------------------------------
// Static (multipart) upload — XHR for progress events
// ---------------------------------------------------------------------------

export type StaticResourceKind = Extract<ResourceKind, 'image' | 'video' | 'audio'>;

export interface UploadStaticResourceInput {
  kind: StaticResourceKind;
  name_i18n: NameI18n;
  folder_id: string | null;
  file: File;
}

export interface UploadStaticResourceOptions {
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

/**
 * Resolves a high-level kind from the browser-supplied MIME type. Matches
 * the server's `assertMimeMatchesKind` policy: only image/*, video/mp4|webm
 * (we don't gate on the subtype here — the server is the authority), and
 * audio/* are accepted. Returns `null` for anything else, which the upload
 * dialog uses to disable the submit button before a network round-trip.
 */
export function detectKindFromMime(mime: string): StaticResourceKind | null {
  const m = mime.toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';
  return null;
}

export function uploadStaticResource(
  input: UploadStaticResourceInput,
  options: UploadStaticResourceOptions = {},
): Promise<ResourceDetail> {
  return new Promise<ResourceDetail>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const url = '/api/v1/admin/resources';

    xhr.open('POST', url, true);
    xhr.withCredentials = true;
    // CSRF mitigation header — see api/client.ts. The browser sets
    // Content-Type to multipart/form-data with a boundary, so we MUST NOT
    // override it here.
    xhr.setRequestHeader('X-Requested-With', 'displaycms-admin');
    xhr.responseType = 'json';

    if (options.signal) {
      const onAbort = (): void => {
        try {
          xhr.abort();
        } catch {
          // ignore
        }
      };
      if (options.signal.aborted) {
        reject(new ApiError(0, { message: 'errors.network', code: 'aborted' }));
        return;
      }
      options.signal.addEventListener('abort', onAbort, { once: true });
    }

    if (options.onProgress) {
      const cb = options.onProgress;
      xhr.upload.addEventListener('progress', (ev: ProgressEvent) => {
        if (ev.lengthComputable && ev.total > 0) {
          const pct = Math.max(0, Math.min(100, Math.round((ev.loaded / ev.total) * 100)));
          cb(pct);
        }
      });
      // Mark complete on body upload finish; the response may still be
      // streaming back when this fires.
      xhr.upload.addEventListener('load', () => cb(100));
    }

    xhr.addEventListener('error', () => {
      reject(
        new ApiError(0, {
          message: 'errors.network',
          code: 'network_error',
        }),
      );
    });

    xhr.addEventListener('abort', () => {
      reject(
        new ApiError(0, {
          message: 'errors.network',
          code: 'aborted',
        }),
      );
    });

    xhr.addEventListener('load', () => {
      const status = xhr.status;
      // jsdom may parse the response as text even with responseType=json;
      // fall back to manual parse.
      let body: unknown = xhr.response;
      if (typeof body === 'string') {
        try {
          body = JSON.parse(body);
        } catch {
          body = undefined;
        }
      }
      if (status >= 200 && status < 300) {
        resolve(body as ResourceDetail);
        return;
      }
      const parsed = (body ?? {}) as Partial<{ message: string; code: string; details: unknown }>;
      reject(
        new ApiError(status, {
          message:
            typeof parsed.message === 'string' && parsed.message.length > 0
              ? parsed.message
              : 'errors.generic',
          ...(typeof parsed.code === 'string' ? { code: parsed.code } : {}),
          ...(parsed.details !== undefined ? { details: parsed.details } : {}),
        }),
      );
    });

    const form = new FormData();
    form.append('kind', input.kind);
    // Multer + the controller accept name_i18n as a JSON-encoded string
    // (see resources.controller.ts: JSON.parse path).
    form.append('name_i18n', JSON.stringify(input.name_i18n));
    form.append('folder_id', input.folder_id ?? '');
    form.append('file', input.file);

    xhr.send(form);
  });
}

// ---------------------------------------------------------------------------
// Dynamic create (JSON)
// ---------------------------------------------------------------------------

export function createDynamicResource(
  body: DynamicResourceCreate,
): Promise<ResourceDetail> {
  return apiPost<ResourceDetail>('/v1/admin/resources/dynamic', body);
}

// ---------------------------------------------------------------------------
// Helpers exposed for the page
// ---------------------------------------------------------------------------

export const RESOURCE_KIND_OPTIONS: ReadonlyArray<ResourceKind> = [
  'image',
  'video',
  'audio',
  'rss_feed',
  'web_page',
];

export const RESOURCE_STATE_OPTIONS: ReadonlyArray<ResourceState> = [
  'draft',
  'pending_review',
  'recommended',
  'approved',
  'revoked',
  'expired',
];

// ---------------------------------------------------------------------------
// Versions list — for the detail page (newest first, paginated server-side).
// ---------------------------------------------------------------------------

export interface ResourceVersionsResponse {
  items: ResourceVersionSummary[];
}

/**
 * Lists every ResourceVersion attached to a Resource, newest first.
 *
 * Endpoint (planned): `GET /api/v1/admin/resources/:id/versions`.
 * The server-side `ResourcesController.get(:id)` only returns the
 * `current_version`; full history is a sibling endpoint to keep the
 * detail payload bounded. Wrapper exists so the detail page and tests
 * have a stable interface even before the route lands server-side.
 */
export function listResourceVersions(
  id: string,
  options?: { signal?: AbortSignal },
): Promise<ResourceVersionsResponse> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  return apiGet<ResourceVersionsResponse>(
    `/v1/admin/resources/${id}/versions`,
    opts,
  );
}

// ---------------------------------------------------------------------------
// Audit timeline + audit-action wrappers.
//
// The server's AuditController exposes the six POST endpoints under
// `/api/v1/admin/resources/:id/<action>`:
//
//   submit-for-review   (no body)        → resources.submit
//   recommend-approve   (no body)        → resources.recommend
//   approve             (ApproveOptions) → resources.approve
//   reject              (RationaleBody)  → resources.reject
//   revoke              (RationaleBody)  → resources.revoke
//   reattest            (ApproveOptions) → resources.reattest
//
// The audit-events GET endpoint is consumed at
// `/api/v1/admin/resources/:id/audit-events` (planned — sibling to the
// action endpoints; lists every AuditEvent attached to the resource's
// current version, newest first).
// ---------------------------------------------------------------------------

export interface AuditEventsResponse {
  items: AuditEventSummary[];
}

export function listResourceAuditEvents(
  id: string,
  options?: { signal?: AbortSignal },
): Promise<AuditEventsResponse> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  return apiGet<AuditEventsResponse>(
    `/v1/admin/resources/${id}/audit-events`,
    opts,
  );
}

/** Approve / reattest body. Both fields are optional; if neither is
 *  provided the server applies its default TTL. `mark_permanent` wins
 *  over `expires_in_seconds`. */
export interface ApproveOptions {
  expires_in_seconds?: number;
  mark_permanent?: boolean;
}

function approveBody(opts: ApproveOptions | undefined): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (opts?.expires_in_seconds !== undefined) {
    body['expires_in_seconds'] = opts.expires_in_seconds;
  }
  if (opts?.mark_permanent !== undefined) {
    body['mark_permanent'] = opts.mark_permanent;
  }
  return body;
}

export function submitForReview(id: string): Promise<unknown> {
  return apiPost<unknown>(`/v1/admin/resources/${id}/submit-for-review`, {});
}

export function recommendApprove(id: string): Promise<unknown> {
  return apiPost<unknown>(`/v1/admin/resources/${id}/recommend-approve`, {});
}

export function approve(id: string, opts?: ApproveOptions): Promise<unknown> {
  return apiPost<unknown>(`/v1/admin/resources/${id}/approve`, approveBody(opts));
}

export function reject(id: string, rationale: string): Promise<unknown> {
  return apiPost<unknown>(`/v1/admin/resources/${id}/reject`, { rationale });
}

export function revoke(id: string, rationale: string): Promise<unknown> {
  return apiPost<unknown>(`/v1/admin/resources/${id}/revoke`, { rationale });
}

// ---------------------------------------------------------------------------
// Update (metadata) — PATCH /v1/admin/resources/:id
// ---------------------------------------------------------------------------
//
// Accepts `{name_i18n?, folder_id?}`. The server requires at least one
// field. Returns the updated resource summary. Used by the resource
// detail page's rename dialog.
export interface UpdateResourceInput {
  name_i18n?: NameI18n;
  folder_id?: string | null;
}

export function updateResource(
  id: string,
  patch: UpdateResourceInput,
): Promise<ResourceSummary> {
  return apiPatch<ResourceSummary>(`/v1/admin/resources/${id}`, patch);
}

export function reattest(id: string, opts?: ApproveOptions): Promise<unknown> {
  return apiPost<unknown>(`/v1/admin/resources/${id}/reattest`, approveBody(opts));
}

// ---------------------------------------------------------------------------
// Signed URL — for admin asset preview (W6 T3).
//
// The server issues a short-TTL (5 min default) HMAC-SHA256-signed URL for
// a ResourceVersion. The admin preview pane uses it to fetch image/video
// bytes directly without re-authentication.
//
// NOTE: `:id` here is the ResourceVersion id, not the Resource id.
// ---------------------------------------------------------------------------

export interface ResourceSignedUrlResponse {
  /** Relative URL including `/api/v1/admin/resources/<id>/raw?expires=...&signature=...` */
  url: string;
  /** ISO 8601 expiry timestamp (5 minutes from issue time by default). */
  expires_at: string;
}

export function getResourceSignedUrl(
  resourceVersionId: string,
  options?: { signal?: AbortSignal },
): Promise<ResourceSignedUrlResponse> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  return apiGet<ResourceSignedUrlResponse>(
    `/v1/admin/resources/${resourceVersionId}/signed-url`,
    opts,
  );
}
